import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import DataTable from "./../../../Layout/Table/TableLayout";
import Navbar from "../../../Shared/ManagerNavbar/Navbar";
import "./Customer.css";
import { FaEye, FaEdit, FaCopy } from "react-icons/fa";
import { baseURL } from "../../../Apiservices/Api";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../../AuthContext/AuthContext";
import Loader from "../../../Loader/Loader";
import { useQuery } from "@tanstack/react-query";

const SalesCustomer = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedTag, setSelectedTag] = useState("");
  const [message, setMessage] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const { authToken, userId } = useContext(AuthContext);

  // ===============================
  // FETCH CUSTOMERS (MANAGER LOGIC)
  // ===============================
  const {
    data: customers = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["manager-customers", userId, selectedTag],
    refetchInterval: 3000, // Refetch every 3 seconds to keep data updated
    queryFn: async () => {
      if (!authToken || !userId) return [];

      // If tag selected → use tag API
      if (selectedTag) {
        const response = await axios.get(
          `${baseURL}/api/tagfilter`,
          {
            params: { tag: selectedTag },
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        return response.data;
      }

      // Fetch all leads
      const leadsResponse = await axios.get(
        `${baseURL}/api/allleads`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const filteredLeads = leadsResponse.data.filter(
        (lead) =>
          lead.managerid == userId &&
          lead.status === "opportunity"
      );

      // Fetch customers
      const customersResponse = await axios.get(
        `${baseURL}/api/customers`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      return customersResponse.data
        .filter((customer) =>
          filteredLeads.some(
            (lead) => lead.customerid == customer.id
          )
        )
        .map((customer) => ({
          ...customer,
          formattedId: `CUS${String(customer.id).padStart(
            4,
            "0"
          )}`,
        }));
    },
    enabled: !!authToken && !!userId,
    keepPreviousData: true,
  });

  // ===============================
  // FETCH TAGS
  // ===============================
  const { data: tags = [] } = useQuery({
    queryKey: ["tags"],
    queryFn: async () => {
      const response = await axios.get(`${baseURL}/api/tags`);
      return response.data;
    },
  });

  // ===============================
  // PAGE FROM URL
  // ===============================
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageFromUrl = parseInt(params.get("page"));

    if (!isNaN(pageFromUrl)) {
      setPageIndex(pageFromUrl - 1);
    }
  }, [location.search]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setMessage("Copied to clipboard!");
      setTimeout(() => setMessage(null), 1000);
    });
  };

  const navigateToLead = (customerId, currentPage) => {
            const params = new URLSearchParams(location.search);
  const pageSize = params.get('pageSize') || '20';


    navigate(
      `/m-customerdetails/${customerId}?page=${currentPage + 1}&pageSize=${pageSize}`
    );
  };

  const navigateToEditLead = (customerId, currentPage) => {
    const params = new URLSearchParams(location.search);
    const pageSize = params.get('pageSize') || '20';
    navigate(
      `/m-editcustomerdetails/${customerId}?page=${currentPage + 1}&pageSize=${pageSize}`
    );
  };

  // ===============================
  // TABLE COLUMNS
  // ===============================
  const columns = useMemo(
    () => [
      { Header: "Customer ID", accessor: "id" },
      {
        Header: "Name",
        accessor: "name",
        Cell: ({ row }) => (
          <div
            style={{
              cursor: "pointer",
              color: "blue",
              textDecoration: "underline",
            }}
            onClick={() =>
              navigateToLead(row.original.id, pageIndex)
            }
          >
            {row.original.name}
          </div>
        ),
      },
      {
        Header: "Mobile",
        accessor: "phone_number",
        Cell: ({ row }) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <a
              href={`https://wa.me/${row.original.phone_number}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "blue" }}
            >
              {row.original.phone_number}
            </a>
            <FaCopy
              style={{
                marginLeft: "8px",
                cursor: "pointer",
                color: "#ff9966",
              }}
              onClick={() =>
                copyToClipboard(row.original.phone_number)
              }
            />
          </div>
        ),
      },
      { Header: "Email", accessor: "email" },
      { Header: "Origin City", accessor: "origincity" },
      {
        Header: "Actions",
        Cell: ({ row }) => {    const currentPage = pageIndex; // Capture current page index
            const params = new URLSearchParams(location.search);
      const pageSize = params.get('pageSize') || '20';

          return (
          <div style={{ display: "flex", gap: "10px" }}>
            <FaEye
              style={{ cursor: "pointer", color: "#ff9966" }}
              onClick={() =>
                navigateToLead(
                  row.original.id,
                  pageIndex, pageSize,currentPage
                )
              }
            />
            <FaEdit
              style={{ cursor: "pointer", color: "#ff9966" }}
              onClick={() =>
                navigateToEditLead(
                  row.original.id,
                  pageIndex,pageSize,currentPage
                )
              }
            />
          </div>
          )
      },
      },
    ],
    [pageIndex,location]
  );

  return (
    <div className="ManagerCustomercontainer">
      <Navbar onToggleSidebar={setCollapsed} />
      <div
        className={`ManagerCustomer ${
          collapsed ? "collapsed" : ""
        }`}
      >
        <div className="ManagerCustomer-container mb-5">
          <div className="ManagerCustomer-table-container">

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3>Customer Details</h3>
              <select
                className="form-select w-25"
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
              >
                <option value="">All Tags</option>
                {tags.map((tag) => (
                  <option key={tag.id} value={tag.value}>
                    {tag.value}
                  </option>
                ))}
              </select>
            </div>

            {message && (
              <div className="alert alert-success">
                {message}
              </div>
            )}

            {isLoading ? (
              <Loader />
            ) : isError ? (
              <div className="text-danger p-4">
                {error?.message}
              </div>
            ) : customers.length === 0 ? (
              <div className="text-center p-5">
                <p>No data available</p>
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={customers}
                pageIndex={pageIndex}
                setPageIndex={setPageIndex}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesCustomer;
